import React from 'react'
import classnames from 'classnames/bind'
import Icon from '../icon'
import Text from '../text'
import { ColorNames } from '../../../constants'
import { AutomationTarget } from '../../../types'
import styles from './styles.scss'

const cx = classnames.bind(styles)

export interface SnackbarProps extends AutomationTarget {
  /** The message content of the snackbar */
  message: React.ReactNode
  /** Optional icon shown to the left of the message (e.g. 'eye-slash') */
  iconName?: string
  /** Optional action label (e.g. 'Undo'). Rendered as a button when provided. */
  actionLabel?: string
  /** Called when the action button is clicked */
  onAction?: () => void
  /** Render as a fixed overlay at the bottom center of the viewport */
  fixed?: boolean
  className?: string
}

/**
 * Snackbar
 *
 * Purely presentational dark notification bar with an optional action
 * (e.g. Undo). Not exported from the design system: consumers open
 * snackbars through the application notifier
 * (`useApplicationNotifications().snackbar.open(...)`), which owns
 * visibility, auto-dismiss, and mounting.
 */
const Snackbar = ({
  message,
  iconName,
  actionLabel,
  onAction,
  fixed = false,
  className,
  elmId = 'snackbar',
}: SnackbarProps) => (
  <div
    data-elm-id={elmId}
    role='status'
    className={cx('container', { fixed }, className)}
  >
    {iconName && (
      <Icon
        elmId={`${elmId}_icon`}
        name={iconName}
        color={ColorNames.White}
        iconStyle='regular'
      />
    )}
    <Text
      elmId={`${elmId}_message`}
      color={ColorNames.White}
      size='tiny'
      weight='heavy'
      className={cx('message')}
    >
      {message}
    </Text>
    {actionLabel && (
      <button
        type='button'
        data-elm-id={`${elmId}_action`}
        className={cx('action')}
        onClick={onAction}
      >
        {actionLabel}
      </button>
    )}
  </div>
)

export default Snackbar
